import { useEditorialAccess } from 'app/context/editorialAccess.context';
import { useEditorialResources } from 'app/context/editorialResource.context';
import { editorialScopeKey, getEditorialCapabilities, getSettledEditorialCapabilities } from 'app/models/editorialCapabilities';
import { runEditorialMutation } from 'app/services/editorialMutation.service';
import React from 'react';

function isSameEditorialScope(previous, current) {
  if (!previous || !current.access || !Array.isArray(current.resources) || !Array.isArray(previous.resources)) return false;
  if (previous.access.updateAccess !== current.access.updateAccess) return false;
  return editorialScopeKey(previous.access, previous.resources) === editorialScopeKey(current.access, current.resources);
}

function isEditorialWritePaused(previous, current) {
  if (!isSameEditorialScope(previous, current)) return false;
  const { access, resources } = current;
  if (!access.revalidating && !resources.some((resource) => resource?.revalidating)) return false;
  return getSettledEditorialCapabilities(access, resources).canWrite;
}

export default function useEditorialCapabilities() {
  const access = useEditorialAccess();
  const resources = useEditorialResources();
  const snapshot = React.useMemo(() => ({ access, resources }), [access, resources]);
  const latest = React.useRef(snapshot);
  latest.current = snapshot;
  const lifecycle = React.useRef(null);
  // El ciclo de vida se ata al ámbito, no a la identidad del estado: una revalidación por foco crea un
  // objeto nuevo sin cambiar el permiso, y abortar ahí descartaba escrituras en vuelo sin avisar.
  const scopeKey = React.useMemo(() => editorialScopeKey(access, resources), [access, resources]);

  React.useEffect(() => {
    const controller = new AbortController();
    lifecycle.current = controller;
    return () => controller.abort();
  }, [scopeKey]);

  const mutate = React.useCallback((operation) => {
    const signal = lifecycle.current?.signal;
    if (!signal) return Promise.resolve({ cancelled: true, error: null });
    return runEditorialMutation({
      getSnapshot: () => latest.current,
      operation,
      signal,
      onAccess: latest.current.access.updateAccess,
      onDenied: latest.current.access.retry,
    });
  }, []);

  const resolvedScope = React.useRef(null);
  const capabilities = getEditorialCapabilities(access, resources);
  const writePaused = !capabilities.canWrite && isEditorialWritePaused(resolvedScope.current, snapshot);
  if (capabilities.canWrite) resolvedScope.current = snapshot;
  else if (!writePaused) resolvedScope.current = null;
  // readOnly describe la vista, no la guarda: durante la pausa por revalidación la UI sigue editable.
  // `canAttemptWrite` es la guarda que deben usar los hooks de escritura: deja pasar la acción durante la
  // pausa y delega la decisión en `mutate`, que autoriza contra un /me fresco. Comprobar `canWrite` a
  // secas descartaba en silencio lo que el usuario acababa de pedir.
  return {
    ...capabilities,
    readOnly: capabilities.readOnly && !writePaused,
    writePaused,
    canAttemptWrite: capabilities.canWrite || writePaused,
    mutate,
  };
}
